import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';
import { PALETTE } from '@fisura/shared';

/**
 * Subte detail for Estación Río de Janeiro: the stationed "A FLORES" train on
 * the north track (matching the walkable colliders in map.ts), plus station
 * dressing — tactile strips, name plates, posters, benches, ceiling strips,
 * rails + train wheels. All decorative (the colliders live in map.ts).
 */

// Train footprint — must match the map.ts 'train-*' colliders.
const TX0 = -30;
const TX1 = 30;
const ZF = 0.7; // far (centre) side
const ZN = 3.5; // near (platform) side
const ZC = (ZF + ZN) / 2;
const ZW = ZN - ZF;
const FLOOR = -3.9;
const ROOF = -1.6;
const TRACK = -4.8;
const DOORS = [-24, -12, 0, 12, 24];
const DH = 0.9; // door half-width

function signTexture(scene: Scene, text: string, bg: string, fg: string, w = 512, h = 128): DynamicTexture {
  const t = new DynamicTexture(`sign-${text}-${w}`, { width: w, height: h }, scene, true);
  const ctx = t.getContext() as unknown as CanvasRenderingContext2D;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = fg;
  ctx.font = `bold ${Math.round(h * 0.5)}px ui-monospace, Menlo, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, h * 0.54);
  t.update();
  return t;
}

function posterTexture(scene: Scene, i: number): DynamicTexture {
  const t = new DynamicTexture(`poster-${i}`, { width: 256, height: 384 }, scene, true);
  const ctx = t.getContext() as unknown as CanvasRenderingContext2D;
  // Palette-only posters: a slate/accent ground with a pale lavender panel.
  const palettes: [string, string][] = [
    ['#46556e', '#c7c9d8'], // slate / light
    ['#cc8042', '#e6e6ed'], // accent / white
    ['#5a5c6e', '#c7c9d8'], // mid-dark / light
    ['#373745', '#cc8042'], // ink / accent
  ];
  const [a, b] = palettes[i % palettes.length]!;
  ctx.fillStyle = a;
  ctx.fillRect(0, 0, 256, 384);
  ctx.fillStyle = b;
  ctx.fillRect(20, 24, 216, 200);
  ctx.fillStyle = a;
  ctx.fillRect(40, 60, 176, 120);
  ctx.fillStyle = b;
  ctx.fillRect(28, 250, 200, 26);
  ctx.fillRect(28, 290, 150, 18);
  ctx.fillRect(28, 320, 180, 18);
  t.update();
  return t;
}

export function buildSubwayDetail(scene: Scene): void {
  const groups = new Map<StandardMaterial, Mesh[]>();
  const cache = new Map<string, StandardMaterial>();
  const mat = (key: string, c: [number, number, number], emissive = false): StandardMaterial => {
    let m = cache.get(key);
    if (!m) {
      m = new StandardMaterial(`tr-${key}`, scene);
      m.diffuseColor = new Color3(c[0], c[1], c[2]);
      m.specularColor = new Color3(0.05, 0.05, 0.05);
      if (emissive) {
        m.emissiveColor = new Color3(c[0], c[1], c[2]);
        m.disableLighting = true;
      }
      cache.set(key, m);
    }
    return m;
  };
  const box = (m: StandardMaterial, cx: number, cy: number, cz: number, sx: number, sy: number, sz: number): void => {
    const mesh = MeshBuilder.CreateBox('tr', { width: sx, height: sy, depth: sz }, scene);
    mesh.position = new Vector3(cx, cy, cz);
    mesh.material = m;
    (groups.get(m) ?? groups.set(m, []).get(m)!).push(mesh);
  };
  const cyl = (m: StandardMaterial, cx: number, cy: number, cz: number, d: number, h: number, axis: 'x' | 'z'): void => {
    const mesh = MeshBuilder.CreateCylinder('trc', { diameter: d, height: h, tessellation: 12 }, scene);
    mesh.rotation[axis === 'x' ? 'z' : 'x'] = Math.PI / 2;
    mesh.position.set(cx, cy, cz);
    mesh.material = m;
    (groups.get(m) ?? groups.set(m, []).get(m)!).push(mesh);
  };
  const signPlane = (tex: DynamicTexture, cx: number, cy: number, cz: number, w: number, h: number, ry: number): void => {
    const m = new StandardMaterial(`tr-sign-${cx}-${cz}-${ry}`, scene);
    m.emissiveTexture = tex;
    m.diffuseTexture = tex;
    m.emissiveColor = new Color3(0.85, 0.85, 0.85);
    m.disableLighting = true;
    const p = MeshBuilder.CreatePlane('sign', { width: w, height: h }, scene);
    p.material = m;
    p.position.set(cx, cy, cz);
    p.rotation.y = ry;
    p.isPickable = false;
    p.freezeWorldMatrix();
  };

  const bodyM = mat('body', PALETTE.light);
  const darkM = mat('dark', PALETTE.dark);
  const blueM = mat('blue', PALETTE.slate); // livery stripe → cool slate
  const yellowM = mat('yellow', PALETTE.accent); // sills/jambs → warm accent
  const glassM = mat('glass', [0.1, 0.11, 0.15]);
  const litM = mat('lit', [0.95, 0.88, 0.78], true); // warm interior glow
  const tailM = mat('tail', PALETTE.accent, true);
  const seatM = mat('seat', PALETTE.slate);
  const poleM = mat('pole', PALETTE.accent);
  const railM = mat('rail', PALETTE.slate);
  const tieM = mat('tie', PALETTE.dark);

  // --- Train side wall: clean rectangular door openings, no curtain/no centre
  //     bar; a continuous header/lintel above the doors closes the gaps. ---
  const side = (z: number, facing: 1 | -1): void => {
    box(bodyM, 0, ROOF - 0.18, z, TX1 - TX0, 0.55, 0.12); // continuous header/lintel
    const segs: [number, number][] = [];
    let wx = TX0;
    for (const d of DOORS) {
      if (d - DH > wx) segs.push([wx, d - DH]);
      wx = d + DH;
    }
    if (TX1 > wx) segs.push([wx, TX1]);
    for (const [a, b] of segs) {
      const len = b - a;
      const cx = (a + b) / 2;
      box(blueM, cx, FLOOR + 1.15, z + facing * 0.05, len, 0.18, 0.06); // blue stripe (between doors only)
      box(bodyM, cx, FLOOR + 0.65, z, len, 1.3, 0.12); // lower body (floor → -2.6)
      box(glassM, cx, -2.32, z + facing * 0.03, len - 0.2, 0.5, 0.08); // window band
      box(yellowM, cx, FLOOR + 0.05, z + facing * 0.05, len, 0.1, 0.08); // yellow sill
    }
    for (const d of DOORS) {
      box(yellowM, d - DH + 0.08, FLOOR + 0.9, z, 0.1, 1.8, 0.14); // jamb (no centre bar)
      box(yellowM, d + DH - 0.08, FLOOR + 0.9, z, 0.1, 1.8, 0.14);
    }
  };
  side(ZN, 1);
  side(ZF, -1);

  // --- Roof, ends, underframe + wheels (so it sits on the rails, not floating) ---
  box(bodyM, 0, ROOF, ZC, TX1 - TX0, 0.2, ZW + 0.1); // roof
  box(litM, 0, ROOF - 0.14, ZC, TX1 - TX0 - 2, 0.04, ZW - 0.6); // interior ceiling lights
  box(darkM, 0, FLOOR - 0.22, ZC, TX1 - TX0 - 0.4, 0.5, ZW - 0.1); // underframe skirt (fills the float)
  for (const ex of [TX0, TX1]) box(bodyM, ex, FLOOR + 1.1, ZC, 0.16, 2.2, ZW);
  // Bogies + wheels resting on the rails (z 1.3 and 2.9).
  for (const bx of [TX0 + 9, TX1 - 9]) {
    box(darkM, bx, -4.25, ZC, 5, 0.4, ZW - 0.4); // bogie frame
    for (const wx of [bx - 1.6, bx + 1.6]) for (const rz of [ZF + 0.6, ZN - 0.6]) cyl(darkM, wx, -4.5, rz, 0.7, 0.3, 'z');
  }

  // --- Interior: floor, benches down BOTH sides, grab poles + ceiling handrail ---
  box(darkM, 0, FLOOR + 0.03, ZC, TX1 - TX0, 0.08, ZW - 0.3); // interior floor
  // Benches in the panels BETWEEN doors only, so they don't block the doorways.
  const seatSegs: [number, number][] = [];
  let sx = TX0 + 0.6;
  for (const d of DOORS) {
    if (d - DH - 0.3 > sx) seatSegs.push([sx, d - DH - 0.3]);
    sx = d + DH + 0.3;
  }
  if (TX1 - 0.6 > sx) seatSegs.push([sx, TX1 - 0.6]);
  for (const z of [ZF + 0.45, ZN - 0.45]) {
    for (const [a, b] of seatSegs) {
      const len = b - a;
      const cx = (a + b) / 2;
      box(seatM, cx, FLOOR + 0.5, z, len, 0.45, 0.55); // cushion
      box(darkM, cx, FLOOR + 0.22, z, len, 0.45, 0.12); // base
      box(seatM, cx, FLOOR + 0.95, z + (z > ZC ? 0.28 : -0.28), len, 0.55, 0.1); // backrest against the wall
    }
  }
  for (const px of [TX0 + 6, -16, -4, 8, 20, TX1 - 6]) {
    box(poleM, px, FLOOR + 1.1, ZC, 0.08, 2.2, 0.08); // vertical grab poles
  }
  box(poleM, 0, ROOF - 0.4, ZC, TX1 - TX0 - 4, 0.06, 0.06); // ceiling handrail
  // "A FLORES" destination sign on the platform side near the front.
  signPlane(signTexture(scene, 'A FLORES', '#10131c', '#e8c24a'), 22, FLOOR + 1.7, ZN + 0.08, 4.2, 0.7, 0);
  // Front cab (Línea A CNR style): big windshield, destination box on top, blue
  // nose stripe, twin corner headlights, dark coupler skirt.
  const fx = TX1;
  box(glassM, fx + 0.12, -2.0, ZC, 0.06, 1.05, ZW - 0.4); // windshield
  box(darkM, fx + 0.13, -1.42, ZC, 0.06, 0.2, ZW - 0.25); // windshield top frame
  box(blueM, fx + 0.12, FLOOR + 1.15, ZC, 0.06, 0.24, ZW - 0.1); // blue nose stripe
  box(darkM, fx + 0.1, FLOOR + 0.18, ZC, 0.14, 0.45, ZW); // coupler / lower skirt
  for (const rz of [ZF + 0.55, ZN - 0.55]) box(litM, fx + 0.16, FLOOR + 0.55, rz, 0.05, 0.3, 0.5); // headlights
  signPlane(signTexture(scene, 'A FLORES', '#101822', '#e8c24a'), fx + 0.18, -1.32, ZC, ZW - 0.7, 0.42, Math.PI / 2);

  // Rear cab (TX0 west end): dark rear with small window, blue stripe, tail lights, coupler.
  const rx = TX0;
  box(glassM, rx - 0.12, -2.1, ZC, 0.06, 0.8, ZW - 0.6); // rear window (smaller than windshield)
  box(darkM, rx - 0.13, -1.65, ZC, 0.06, 0.2, ZW - 0.4); // window top frame
  box(blueM, rx - 0.12, FLOOR + 1.15, ZC, 0.06, 0.24, ZW - 0.1); // blue rear stripe
  box(darkM, rx - 0.1, FLOOR + 0.18, ZC, 0.14, 0.45, ZW); // coupler / lower skirt
  for (const rz of [ZF + 0.55, ZN - 0.55]) box(tailM, rx - 0.16, FLOOR + 0.55, rz, 0.05, 0.3, 0.5); // tail lights

  // --- Rails on both tracks (north has the train; south is empty) ---
  for (const [za, zb] of [[ZF + 0.6, ZN - 0.6], [-ZN + 0.6, -ZF - 0.6]] as [number, number][]) {
    for (const rz of [za, zb]) box(railM, 0, TRACK + 0.08, rz, 144, 0.12, 0.14);
    for (let x = -70; x <= 70; x += 2.2) box(tieM, x, TRACK + 0.03, (za + zb) / 2, 0.4, 0.06, Math.abs(zb - za) + 0.6);
  }

  // --- Station dressing ---
  const tactileM = mat('tactile', PALETTE.accent);
  box(tactileM, 0, FLOOR + 0.02, 3.3, 144, 0.06, 0.4); // north platform edge
  box(tactileM, 0, FLOOR + 0.02, -3.3, 144, 0.06, 0.4); // south platform edge
  // Big "Río de Janeiro" name plates + framed posters on both outer walls.
  const nameTex = signTexture(scene, 'Río de Janeiro', '#0a2a6a', '#ffffff', 1024, 160);
  const frameM = mat('frame', [0.12, 0.12, 0.14]);
  for (const [zWall, ry] of [[8.15, Math.PI], [-8.15, 0]] as [number, number][]) {
    for (const x of [-40, 0, 40]) signPlane(nameTex, x, -1.5, zWall, 9, 1.4, ry);
    for (let i = 0; i < 5; i++) {
      const x = -56 + i * 28;
      if (Math.abs(x) < 8) continue; // skip the stair mouths roughly
      box(frameM, x, -2.7, zWall + (ry === 0 ? -0.04 : 0.04), 2.0, 2.8, 0.08); // poster frame
      signPlane(posterTexture(scene, i), x, -2.7, zWall + (ry === 0 ? -0.09 : 0.09), 1.7, 2.5, ry);
    }
  }
  // Benches + trash bins on both platforms.
  for (const z of [6.6, -6.6]) {
    for (const x of [-40, 0, 40]) {
      box(mat('pbench', PALETTE.accent), x, FLOOR + 0.45, z, 3.0, 0.18, 0.7); // seat
      box(darkM, x, FLOOR + 0.22, z, 3.0, 0.45, 0.12);
      box(mat('pbench', [0.55, 0.42, 0.2]), x, FLOOR + 0.75, z + Math.sign(z) * 0.3, 3.0, 0.5, 0.1); // back on the wall side → seat faces the tracks
      box(darkM, x + 5, FLOOR + 0.5, z, 0.7, 1.0, 0.7); // trash bin nearby
    }
  }
  // Fluorescent ceiling strips down each platform.
  for (const z of [5.8, -5.8]) {
    for (let x = -64; x <= 64; x += 12) box(litM, x, -0.7, z, 6, 0.2, 0.6);
  }

  for (const [m, meshes] of groups) {
    const merged = meshes.length === 1 ? meshes[0]! : Mesh.MergeMeshes(meshes, true, true);
    if (merged) {
      merged.material = m;
      merged.isPickable = false;
      merged.freezeWorldMatrix();
    }
  }
}
